<template>
  <div class="store-profile-container">
    <!-- 左侧操作按钮栏 -->
    <div class="sidebar">
      <div class="sidebar-header">
        <i class="el-icon-s-shop"></i>
        <span>店铺管理</span>
      </div>

      <div class="sidebar-actions">
        <el-button
            type="text"
            @click="showAllIcons"
            class="sidebar-btn icon-view-btn"
        >
          <i class="el-icon-star-on"></i>
          <span>查看所有图标</span>
        </el-button>

        <el-button
            type="primary"
            @click="saveStoreProfile"
            :loading="loading"
            class="sidebar-btn primary-btn"
        >
          <i class="el-icon-check"></i>
          <span>保存资料</span>
        </el-button>

        <el-button
            @click="previewStore"
            :disabled="!form.id"
            class="sidebar-btn"
        >
          <i class="el-icon-view"></i>
          <span>预览店铺</span>
        </el-button>

        <el-button
            @click="gotoQualification"
            :disabled="!form.id"
            class="sidebar-btn"
        >
          <i class="el-icon-document"></i>
          <span>资质管理</span>
        </el-button>

        <el-button
            type="info"
            @click="openImageManager"
            class="sidebar-btn"
        >
          <i class="el-icon-picture"></i>
          <span>图片库</span>
        </el-button>
      </div>
    </div>

    <!-- 右侧主内容区域 -->
    <div class="main-content">
      <el-card class="profile-card">
        <template slot="header">
          <div class="card-header">
            <span class="card-title">店铺资料管理</span>
          </div>
        </template>

        <div v-loading="loading" class="profile-content">
          <div v-if="userInfo" class="user-info-tip">
            <el-alert
                :title="userInfo.isSuperAdmin ? `超级管理员 - 编辑店铺ID: ${form.id || '新建'}` : `店铺管理员 - 您的店铺ID: ${userInfo.merchantId}`"
                :type="userInfo.isSuperAdmin ? 'info' : 'success'"
                show-icon
                :closable="false"
            />
          </div>

          <el-form :model="form" :rules="rules" ref="formRef" label-width="120px">
            <!-- 基础信息 -->
            <div class="section">
              <h3 class="section-title">
                <i class="el-icon-s-shop"></i>
                基础信息
              </h3>

              <el-form-item label="店铺名称" prop="name" required>
                <el-input
                    v-model="form.name"
                    placeholder="请输入店铺名称"
                    maxlength="50"
                    show-word-limit
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>

              <el-form-item label="店铺主图" prop="imageId" required>
                <div class="main-image-manager">
                  <div class="image-placeholder-container">
                    <image-placeholder
                        @click="selectMainImage"
                        :image="form.imageId ? { id: form.imageId, url: mainImageUrl } : null"
                        :show-delete="!!form.imageId"
                        @delete="removeMainImage"
                        :style="{width: '200px', height: '120px'}"
                    />
                  </div>
                  <div class="image-actions">
                    <el-button size="mini" @click="selectMainImage">
                      <i class="el-icon-picture-outline"></i> {{ form.imageId ? '更换图片' : '选择图片' }}
                    </el-button>
                    <el-button size="mini" type="primary" @click="openImageUploader">
                      <i class="el-icon-upload"></i> 上传新图片
                    </el-button>
                  </div>
                  <div class="image-tips">
                    <p>• 主图将显示在店铺列表和详情页顶部</p>
                    <p>• 支持 JPG、PNG、GIF、WebP 格式</p>
                    <p>• 建议尺寸：750×400像素</p>
                    <p v-if="form.imageId">• 图片ID: {{ form.imageId }}</p>
                  </div>
                </div>
              </el-form-item>

              <el-form-item label="营业状态" prop="status" required>
                <el-radio-group v-model="form.status">
                  <el-radio :label="1">
                    <el-tag type="success" size="small">正常营业</el-tag>
                  </el-radio>
                  <el-radio :label="2">
                    <el-tag type="warning" size="small">暂停营业</el-tag>
                  </el-radio>
                  <el-radio :label="3">
                    <el-tag type="info" size="small">永久关闭</el-tag>
                  </el-radio>
                </el-radio-group>
              </el-form-item>

              <div class="category-cascade">
                <el-form-item label="行业分类" prop="industryCategoryId" required>
                  <el-select
                      v-model="form.industryCategoryId"
                      placeholder="请选择行业分类"
                      filterable
                      clearable
                      @change="handleIndustryChange"
                      style="width: 100%;"
                      :loading="categoryLoading"
                  >
                    <el-option
                        v-for="industry in industryOptions"
                        :key="industry.id"
                        :label="industry.name"
                        :value="industry.id"
                    />
                  </el-select>
                </el-form-item>

                <el-form-item label="店铺分类" prop="storeCategoryId" required>
                  <el-select
                      v-model="form.storeCategoryId"
                      placeholder="请先选择行业分类"
                      filterable
                      clearable
                      :disabled="!form.industryCategoryId"
                      @change="handleStoreCategoryChange"
                      style="width: 100%;"
                      :loading="categoryLoading"
                  >
                    <el-option
                        v-for="category in storeCategoryOptions"
                        :key="category.id"
                        :label="category.name"
                        :value="category.id"
                    />
                  </el-select>
                </el-form-item>

                <el-form-item label="品类细分" prop="productCategoryId">
                  <el-select
                      v-model="form.productCategoryId"
                      placeholder="请先选择店铺分类"
                      filterable
                      clearable
                      :disabled="!form.storeCategoryId"
                      style="width: 100%;"
                      :loading="categoryLoading"
                  >
                    <el-option
                        v-for="product in productCategoryOptions"
                        :key="product.id"
                        :label="product.name"
                        :value="product.id"
                    />
                  </el-select>
                </el-form-item>
              </div>

              <el-form-item label="营业时间" prop="businessHours">
                <el-input
                    v-model="form.businessHours"
                    placeholder="例如：09:00-22:00 或 全天24小时"
                    maxlength="100"
                    clearable
                    style="width: 100%;"
                />
              </el-form-item>

              <el-form-item label="人均价格" prop="avgPrice">
                <div class="price-input">
                  <el-input-number
                      v-model="form.avgPrice"
                      :min="0"
                      :max="99999"
                      :precision="2"
                      placeholder="请输入人均价格"
                      @change="handlePriceChange"
                      style="width: 200px;"
                  />
                  <span class="price-unit">元</span>
                  <span class="price-tip">（直接输入元单位，如：10.50表示10.5元）</span>
                </div>
              </el-form-item>
            </div>

            <!-- 店铺介绍 -->
            <div class="section">
              <h3 class="section-title">
                <i class="el-icon-document"></i>
                店铺介绍
              </h3>
              <div class="introduction-editor">
                <el-form-item prop="introduction">
                  <el-input
                      v-model="form.introduction"
                      type="textarea"
                      :rows="5"
                      placeholder="请输入店铺详细介绍..."
                      maxlength="2000"
                      show-word-limit
                      resize="none"
                  />
                </el-form-item>
                <div class="editor-tips">
                  <p>• 详细介绍店铺特色、主打产品、服务理念等</p>
                  <p>• 建议包含营业时间、联系方式等基本信息</p>
                  <p>• 可突出店铺优势和特色服务</p>
                </div>
              </div>

              <el-form-item label="店铺公告" prop="notice">
                <el-input
                    v-model="form.notice"
                    type="textarea"
                    :rows="3"
                    placeholder="请输入店铺公告或最新通知..."
                    maxlength="500"
                    show-word-limit
                    resize="none"
                />
              </el-form-item>
            </div>

            <!-- 标签管理 -->
            <div class="section">
              <h3 class="section-title">
                <i class="el-icon-collection"></i>
                店铺标签
              </h3>
              <div class="tags-management">
                <div class="tags-management-row">
                  <div class="tags-column selected-tags-column">
                    <div class="tags-header">
                      <span class="tags-title">已选择标签</span>
                      <el-tag type="info" size="small">
                        {{ selectedTags.length }}/10
                      </el-tag>
                    </div>
                    <div class="tags-list">
                      <el-tag
                          v-for="tag in selectedTags"
                          :key="tag.id"
                          closable
                          :type="getTagType(tag.typeCode)"
                          @close="removeTag(tag.id)"
                          class="tag-item"
                      >
                        {{ tag.name }}
                        <span class="tag-type-text">({{ tag.typeText }})</span>
                      </el-tag>
                      <el-empty
                          v-if="selectedTags.length === 0"
                          :image-size="80"
                          description="暂无标签，请在右侧选择"
                      />
                    </div>
                  </div>

                  <div class="tags-column available-tags-column">
                    <div class="tags-header">
                      <span class="tags-title">可选标签</span>
                      <span class="tags-tip">点击标签进行选择/取消</span>
                    </div>
                    <div class="tags-selector">
                      <div class="tag-categories">
                        <el-tabs v-model="activeTagCategory" @tab-click="handleTagCategoryChange">
                          <el-tab-pane
                              v-for="category in tagCategories"
                              :key="category.typeCode"
                              :label="category.name"
                              :name="category.typeCode.toString()"
                          >
                            <div class="category-tags">
                              <el-tag
                                  v-for="tag in category.tags"
                                  :key="tag.id"
                                  :type="isTagSelected(tag.id) ? 'success' : ''"
                                  :class="{ 'tag-selected': isTagSelected(tag.id) }"
                                  class="tag-option"
                                  @click="toggleTag(tag)"
                              >
                                {{ tag.name }}
                                <i v-if="isTagSelected(tag.id)" class="el-icon-check selected-icon"></i>
                                <span class="tag-type-badge">{{ category.name }}</span>
                              </el-tag>
                            </div>
                          </el-tab-pane>
                        </el-tabs>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 位置信息 -->
            <div class="section">
              <h3 class="section-title">
                <i class="el-icon-location-information"></i>
                位置信息
              </h3>

              <el-form-item label="详细地址" prop="address" required>
                <div class="address-input">
                  <el-input
                      v-model="form.address"
                      placeholder="请输入详细地址"
                      maxlength="255"
                      clearable
                  >
                    <el-button slot="append" @click="openMapPicker">
                      <i class="el-icon-map-location"></i>
                      地图选点
                    </el-button>
                  </el-input>
                </div>
              </el-form-item>

              <div class="coordinates">
                <el-form-item label="经度" prop="longitude">
                  <el-input-number
                      v-model="form.longitude"
                      :min="-180"
                      :max="180"
                      :precision="9"
                      :step="0.000001"
                      placeholder="请在地图选点"
                      :controls="false"
                      readonly
                      style="width: 200px;"
                  />
                </el-form-item>
                <el-form-item label="纬度" prop="latitude">
                  <el-input-number
                      v-model="form.latitude"
                      :min="-90"
                      :max="90"
                      :precision="9"
                      :step="0.000001"
                      placeholder="请在地图选点"
                      :controls="false"
                      readonly
                      style="width: 200px;"
                  />
                </el-form-item>
              </div>

              <div class="map-preview" v-if="showMapPreview && !mapSDKFatalError">
                <div id="map-container" style="width: 100%; height: 300px;"></div>
                <div class="map-tips">
                  <i class="el-icon-info"></i>
                  经纬度：{{ form.longitude ? form.longitude.toFixed(6) : '--' }}, {{ form.latitude ? form.latitude.toFixed(6) : '--' }}
                </div>
              </div>

              <div class="map-preview" v-else-if="mapSDKFatalError">
                <el-alert
                    title="地图功能暂时不可用"
                    type="warning"
                    description="请手动填写详细地址和经纬度信息"
                    show-icon
                    :closable="false"
                />
              </div>
            </div>

          </el-form>
        </div>
      </el-card>

      <!-- 地图选择对话框 -->
      <el-dialog
          title="选择店铺位置"
          :visible.sync="mapDialogVisible"
          width="80%"
          top="5vh"
          :close-on-click-modal="false"
          append-to-body
          @opened="handleMapDialogOpened"
          @closed="handleMapDialogClosed"
      >
        <div id="picker-map-container" style="width: 100%; height: 500px; position: relative;"></div>
        <div style="margin-top: 16px; display: flex; justify-content: space-between; align-items: center;">
          <div>
            <span class="el-text-secondary">当前选中坐标：</span>
            <span style="font-weight: 500;">
              经度: {{ tempLongitude ? tempLongitude.toFixed(6) : form.longitude ? form.longitude.toFixed(6) : '--' }},
              纬度: {{ tempLatitude ? tempLatitude.toFixed(6) : form.latitude ? form.latitude.toFixed(6) : '--' }}
            </span>
          </div>
          <div style="color: #999; font-size: 12px;">
            <i class="el-icon-info"></i> 点击地图选择位置，然后点击右下角"确认选点"
          </div>
        </div>
      </el-dialog>

      <!-- 图标查看对话框 -->
      <el-dialog
          title="所有图标"
          :visible.sync="iconDialog.visible"
          width="90%"
          top="5vh"
          append-to-body
          class="icon-viewer-dialog"
          @opened="handleIconDialogOpened"
      >
        <div class="icon-viewer-container">
          <div class="icon-stats">
            <el-tag type="info" size="medium">
              <i class="el-icon-picture"></i> 共 {{ iconDialog.total }} 个图标
            </el-tag>
            <el-tag type="success" size="medium">
              <i class="el-icon-view"></i> 总使用次数: {{ iconDialog.totalUsageCount }}
            </el-tag>
            <el-tag type="warning" size="medium">
              <i class="el-icon-collection"></i> 当前页: {{ iconDialog.list.length }} 个
            </el-tag>
          </div>

          <div class="icon-filter">
            <el-input
                v-model="iconDialog.filter.search"
                placeholder="搜索图标名称、描述、分类..."
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="loadIcons"
                @clear="loadIcons"
                style="width: 300px;"
            />
            <el-button
                type="primary"
                @click="loadIcons"
                icon="el-icon-search"
                style="margin-left: 10px;"
            >
              搜索
            </el-button>
            <el-button
                @click="resetIconFilter"
                icon="el-icon-refresh"
            >
              重置
            </el-button>
          </div>

          <div class="icon-list-container">
            <div v-if="iconDialog.loading" class="icon-loading">
              <i class="el-icon-loading"></i>
              加载中...
            </div>

            <div v-else-if="iconDialog.list.length === 0" class="icon-empty">
              <i class="el-icon-picture-outline"></i>
              <p>暂无图标数据</p>
            </div>

            <div v-else class="icon-grid">
              <div
                  v-for="icon in iconDialog.list"
                  :key="icon.id"
                  class="icon-item"
                  :class="{ 'selected': iconDialog.selectedIconId === icon.id }"
                  @click="selectIcon(icon)"
              >
                <div class="icon-display">
                  <div class="icon-char">
                    {{ icon.iconChar || '?' }}
                  </div>
                </div>
                <div class="icon-info">
                  <div class="icon-name" :title="icon.iconName">
                    {{ icon.iconName }}
                  </div>
                  <div class="icon-category">
                    <span class="main-category">{{ icon.mainCategory }}</span>
                    <span class="sub-category">{{ icon.subCategory }}</span>
                  </div>
                  <div class="icon-description" :title="icon.description">
                    {{ icon.description }}
                  </div>
                  <div class="icon-meta">
                    <span class="usage">
                      <i class="el-icon-view"></i> {{ icon.usageCount || 0 }}
                    </span>
                    <span class="industry">
                      <i class="el-icon-s-shop"></i> {{ icon.industryName }}
                    </span>
                  </div>
                </div>
                <div v-if="iconDialog.selectedIconId === icon.id" class="selected-indicator">
                  <i class="el-icon-check"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="icon-pagination">
            <el-pagination
                @size-change="handleIconPageSizeChange"
                @current-change="handleIconPageChange"
                :current-page="iconDialog.page"
                :page-sizes="[24, 48, 96, 200]"
                :page-size="iconDialog.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="iconDialog.total"
                background
            />
          </div>
        </div>

        <span slot="footer" class="dialog-footer">
          <div class="selected-icon-info" v-if="iconDialog.selectedIcon">
            <span>已选择: <strong>{{ iconDialog.selectedIcon.iconName }}</strong></span>
            <span class="icon-preview">{{ iconDialog.selectedIcon.iconChar || '?' }}</span>
          </div>
          <div>
            <el-button @click="iconDialog.visible = false">
              取消
            </el-button>
            <el-button
                type="primary"
                @click="useSelectedIcon"
                :disabled="!iconDialog.selectedIcon"
            >
              使用此图标
            </el-button>
          </div>
        </span>
      </el-dialog>
    </div>

    <!-- 图片选择器 -->
    <image-selector
        :visible.sync="imageSelectorVisible"
        :max-count="1"
        @select="handleImageSelect"
        @close="handleImageSelectorClose"
    />

    <!-- 图片上传器 -->
    <image-uploader
        :visible.sync="imageUploaderVisible"
        @upload="handleImageUploadComplete"
        :category="imageUploaderCategory"
    />
  </div>
</template>

<script>
import api from "@/utils/api"
import ImageSelector from "@/components/ImageSelector"
import ImageUploader from "@/components/ImageUploader"
import ImagePlaceholder from "@/components/ImagePlaceholder"

export default {
  name: 'StoreProfile',
  components: {
    ImageSelector,
    ImageUploader,
    ImagePlaceholder
  },
  data() {
    return {
      loading: false,
      loadingUserInfo: false,
      isInitializing: false,
      categoryLoading: false,

      userInfo: null,

      form: {
        id: null,
        name: '',
        imageId: null,
        status: 1,
        industryCategoryId: null,
        storeCategoryId: null,
        productCategoryId: null,
        businessHours: '',
        avgPrice: 0,
        introduction: '',
        notice: '',
        address: '',
        longitude: null,
        latitude: null,
        starRating: 0,
        monthlySales: 0,
        ratingCount: 0,
        productCount: 0,
        viewCount: 0,
        collectCount: 0,
        recommendScore: 0
      },

      rules: {
        name: [
          { required: true, message: '请输入店铺名称', trigger: 'blur' },
          { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
        ],
        imageId: [
          { required: true, message: '请选择店铺主图', trigger: 'change' }
        ],
        status: [
          { required: true, message: '请选择营业状态', trigger: 'change' }
        ],
        address: [
          { required: true, message: '请输入详细地址', trigger: 'blur' }
        ],
        businessHours: [
          { required: false, message: '请输入营业时间', trigger: 'blur' }
        ],
        avgPrice: [
          { type: 'number', min: 0, max: 9999999, message: '价格必须在0-9999999之间', trigger: 'blur' }
        ],
        industryCategoryId: [
          { required: true, message: '请选择行业分类', trigger: 'change' }
        ],
        storeCategoryId: [
          { required: true, message: '请选择店铺分类', trigger: 'change' }
        ]
      },

      categoryTree: [],
      industryOptions: [],
      storeCategoryOptions: [],
      productCategoryOptions: [],

      mainImageUrl: '',

      // 图片选择器状态
      imageSelectorVisible: false,

      // 图片上传器状态
      imageUploaderVisible: false,
      imageUploaderCategory: null,

      selectedTags: [],
      tagCategories: [],
      activeTagCategory: '1',

      // 腾讯地图相关
      showMapPreview: false,
      mapDialogVisible: false,
      mapInstance: null,
      pickerMapInstance: null,
      mapMarker: null,
      pickerMapMarker: null,
      tempLongitude: null,
      tempLatitude: null,
      tencentMapKey: 'NGABZ-U2D63-7O63E-OLKRJ-I6OG5-W5BQ7',
      tencentMapVersion: '1.8.0.2',
      isTencentMapLoaded: false,
      isTencentMapLoading: false,
      mapSDKFatalError: false,
      isMapInitializing: false,
      isUpdatingPosition: false,
      mapDebounceTimer: null,
      tencentMapSDKUrl: 'https://map.qq.com/api/gljs',

      // 请求去重
      pendingRequests: new Map(),

      // 图标对话框
      iconDialog: {
        visible: false,
        loading: false,
        list: [],
        page: 1,
        pageSize: 24,
        total: 0,
        totalUsageCount: 0,
        selectedIconId: null,
        selectedIcon: null,
        filter: {
          search: ''
        }
      }
    }
  },

  computed: {
    currentStoreId() {
      if (this.$route.query.id) {
        return Number(this.$route.query.id)
      }
      if (this.userInfo && this.userInfo.merchantId) {
        return Number(this.userInfo.merchantId)
      }
      return null
    },

    isSuperAdmin() {
      return this.userInfo && this.userInfo.isSuperAdmin === true
    }
  },

  watch: {
    '$route.query.id': {
      handler(newId, oldId) {
        if (newId !== oldId && !this.isInitializing) {
          this.loadStoreData()
        }
      }
    },
    'form.imageId': {
      immediate: true,
      handler(newImageId) {
        if (newImageId && !this.mainImageUrl) {
          this.loadMainImage(newImageId)
        } else if (!newImageId) {
          this.mainImageUrl = ''
        }
      }
    },
    'form.longitude'(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.isMapInitializing && !this.isUpdatingPosition) {
        clearTimeout(this.mapDebounceTimer)
        this.mapDebounceTimer = setTimeout(() => {
          this.checkAndInitMap()
        }, 300)
      }
    },
    'form.latitude'(newVal, oldVal) {
      if (newVal && newVal !== oldVal && !this.isMapInitializing && !this.isUpdatingPosition) {
        clearTimeout(this.mapDebounceTimer)
        this.mapDebounceTimer = setTimeout(() => {
          this.checkAndInitMap()
        }, 300)
      }
    }
  },

  mounted() {
    this.initializePage()
  },

  beforeDestroy() {
    this.destroyMaps()
    if (this.mapDebounceTimer) {
      clearTimeout(this.mapDebounceTimer)
    }
  },

  methods: {
    // ============== 请求去重方法 ==============
    async deduplicateRequest(key, requestFn) {
      if (this.pendingRequests.has(key)) {
        console.log(`请求 ${key} 正在进行中，等待结果`)
        return this.pendingRequests.get(key)
      }

      const promise = requestFn().finally(() => {
        this.pendingRequests.delete(key)
      })

      this.pendingRequests.set(key, promise)
      return promise
    },

    // ============== 腾讯地图相关方法 ==============

    async loadTencentMapSDK() {
      if (this.mapSDKFatalError) {
        return false
      }

      if (this.isTencentMapLoaded) {
        return true
      }

      if (this.isTencentMapLoading) {
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (this.isTencentMapLoaded) {
              clearInterval(checkInterval)
              resolve(true)
            } else if (this.mapSDKFatalError) {
              clearInterval(checkInterval)
              resolve(false)
            }
          }, 100)
        })
      }

      this.isTencentMapLoading = true

      return new Promise((resolve) => {
        if (window.TMap) {
          this.isTencentMapLoaded = true
          this.isTencentMapLoading = false
          console.log('腾讯地图SDK已存在')
          resolve(true)
          return
        }

        const timeout = setTimeout(() => {
          console.error('腾讯地图SDK加载超时')
          this.isTencentMapLoading = false
          this.mapSDKFatalError = true
          resolve(false)
        }, 10000)

        const script = document.createElement('script')
        script.id = 'tencent-map-script'
        script.src = `${this.tencentMapSDKUrl}?v=${this.tencentMapVersion}&key=${this.tencentMapKey}&callback=init`
        script.onload = () => {
          clearTimeout(timeout)
          console.log('腾讯地图SDK加载完成')
          setTimeout(() => {
            if (window.TMap) {
              this.isTencentMapLoaded = true
              this.isTencentMapLoading = false
              resolve(true)
            } else {
              console.error('地图SDK初始化失败：TMap对象不存在')
              this.mapSDKFatalError = true
              this.isTencentMapLoading = false
              resolve(false)
            }
          }, 500)
        }
        script.onerror = (error) => {
          clearTimeout(timeout)
          console.error('腾讯地图SDK加载失败:', error)
          this.mapSDKFatalError = true
          this.isTencentMapLoading = false
          resolve(false)
        }
        document.body.appendChild(script)
      })
    },

    async checkAndInitMap() {
      if (this.isMapInitializing) {
        console.log('地图正在初始化中，跳过')
        return
      }

      if (this.form.longitude && this.form.latitude) {
        try {
          this.isMapInitializing = true
          const loaded = await this.loadTencentMapSDK()
          if (!loaded) {
            console.warn('地图SDK加载失败，跳过地图初始化')
            this.showMapPreview = false
            return
          }
          this.showMapPreview = true
          await this.$nextTick()
          setTimeout(() => {
            this.initMapPreview()
          }, 100)
        } catch (error) {
          console.error('地图SDK加载失败:', error)
          this.showMapPreview = false
        } finally {
          setTimeout(() => {
            this.isMapInitializing = false
          }, 500)
        }
      }
    },

    initMapPreview() {
      try {
        if (!window.TMap) {
          console.warn('腾讯地图SDK未加载完成')
          return
        }

        const mapContainer = document.getElementById('map-container')
        if (!mapContainer) {
          console.warn('地图容器不存在')
          return
        }

        if (this.mapInstance) {
          console.log('地图已初始化，更新标记位置')
          if (this.mapMarker) {
            this.mapMarker.setGeometries([{
              id: 'marker',
              styleId: 'default',
              position: new TMap.LatLng(this.form.latitude, this.form.longitude)
            }])
            this.mapInstance.setCenter(new TMap.LatLng(this.form.latitude, this.form.longitude))
          }
          return
        }

        this.mapInstance = new TMap.Map(mapContainer, {
          zoom: 16,
          center: new TMap.LatLng(this.form.latitude, this.form.longitude),
          viewMode: '2D'
        })

        this.mapMarker = new TMap.MultiMarker({
          map: this.mapInstance,
          styles: {
            default: new TMap.MarkerStyle({
              width: 25,
              height: 35,
              anchor: { x: 12, y: 35 }
            })
          },
          geometries: [{
            id: 'marker',
            styleId: 'default',
            position: new TMap.LatLng(this.form.latitude, this.form.longitude)
          }]
        })

        console.log('地图预览初始化成功')

      } catch (error) {
        console.error('初始化地图预览失败:', error)
        this.showMapPreview = false
      }
    },

    async openMapPicker() {
      try {
        const loaded = await this.loadTencentMapSDK()
        if (!loaded) {
          this.$message.warning('地图功能暂时不可用，请手动输入地址')
          return
        }
        this.tempLongitude = this.form.longitude
        this.tempLatitude = this.form.latitude
        this.mapDialogVisible = true
      } catch (error) {
        console.error('加载地图SDK失败:', error)
        this.$message.warning('地图功能暂时不可用，请手动输入地址')
      }
    },

    handleMapDialogOpened() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.initPickerMap()
        }, 100)
      })
    },

    handleMapDialogClosed() {
      this.destroyPickerMap()
    },

    initPickerMap() {
      try {
        if (!window.TMap) {
          throw new Error('腾讯地图SDK未加载')
        }

        this.destroyPickerMap()

        const pickerContainer = document.getElementById('picker-map-container')
        if (!pickerContainer) {
          throw new Error('选择器地图容器不存在')
        }

        pickerContainer.style.position = 'relative'

        let center = new TMap.LatLng(39.90923, 116.397428)
        if (this.form.longitude && this.form.latitude) {
          center = new TMap.LatLng(this.form.latitude, this.form.longitude)
        }

        this.pickerMapInstance = new TMap.Map(pickerContainer, {
          zoom: 16,
          center: center,
          viewMode: '2D'
        })

        this.pickerMapMarker = new TMap.MultiMarker({
          map: this.pickerMapInstance,
          styles: {
            default: new TMap.MarkerStyle({
              width: 25,
              height: 35,
              anchor: { x: 12, y: 35 }
            })
          },
          geometries: [{
            id: 'marker',
            styleId: 'default',
            position: center
          }]
        })

        this.pickerMapInstance.on('click', (event) => {
          this.updatePickerMarker(event.latLng.lng, event.latLng.lat)
        })

        this.$nextTick(() => {
          this.addConfirmButtonToMap()
        })

        console.log('选择器地图初始化成功')

      } catch (error) {
        console.error('初始化选择器地图失败:', error)
        this.$message.error('地图加载失败，请重试')
      }
    },

    async updatePickerMarker(lng, lat) {
      if (!this.pickerMapMarker) return
      if (this.isUpdatingPosition) return

      this.isUpdatingPosition = true

      try {
        this.pickerMapMarker.setGeometries([{
          id: 'marker',
          styleId: 'default',
          position: new TMap.LatLng(lat, lng)
        }])

        this.tempLongitude = lng
        this.tempLatitude = lat
      } finally {
        setTimeout(() => {
          this.isUpdatingPosition = false
        }, 100)
      }
    },

    addConfirmButtonToMap() {
      if (!this.pickerMapInstance) return

      const existingButton = document.getElementById('map-confirm-button')
      if (existingButton) {
        existingButton.remove()
      }

      const pickerContainer = document.getElementById('picker-map-container')
      if (!pickerContainer) return

      const confirmButton = document.createElement('div')
      confirmButton.id = 'map-confirm-button'
      confirmButton.style.cssText = `
        position: absolute;
        bottom: 20px;
        right: 20px;
        z-index: 1000;
        background-color: #409eff;
        color: white;
        padding: 10px 20px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        text-align: center;
        transition: all 0.3s;
        user-select: none;
        display: flex;
        align-items: center;
        gap: 6px;
      `
      confirmButton.innerHTML = '<span style="font-size: 16px;">✓</span> 确认选点'

      confirmButton.onmouseenter = () => {
        confirmButton.style.backgroundColor = '#66b1ff'
      }
      confirmButton.onmouseleave = () => {
        confirmButton.style.backgroundColor = '#409eff'
      }

      confirmButton.onclick = (e) => {
        e.stopPropagation()
        this.confirmMapSelection()
      }

      pickerContainer.style.position = 'relative'
      pickerContainer.appendChild(confirmButton)
    },

    confirmMapSelection() {
      if (!this.pickerMapMarker) {
        this.$message.warning('请先选择位置')
        return
      }

      const geometries = this.pickerMapMarker.getGeometries()
      if (!geometries || geometries.length === 0) {
        this.$message.warning('请先选择位置')
        return
      }

      const position = geometries[0].position
      this.form.longitude = position.lng
      this.form.latitude = position.lat

      this.mapDialogVisible = false
      this.showMapPreview = true

      this.$nextTick(() => {
        if (this.mapInstance && this.mapMarker) {
          this.mapMarker.setGeometries([{
            id: 'marker',
            styleId: 'default',
            position: new TMap.LatLng(this.form.latitude, this.form.longitude)
          }])
          this.mapInstance.setCenter(new TMap.LatLng(this.form.latitude, this.form.longitude))
        } else {
          this.initMapPreview()
        }
      })

      this.$message.success('位置选择成功，经纬度已更新')
    },

    destroyPickerMap() {
      try {
        const confirmButton = document.getElementById('map-confirm-button')
        if (confirmButton) {
          confirmButton.remove()
        }

        if (this.pickerMapInstance) {
          this.pickerMapInstance.destroy()
          this.pickerMapInstance = null
        }
        this.pickerMapMarker = null
      } catch (error) {
        console.warn('销毁选择器地图失败:', error)
      }
    },

    destroyMaps() {
      try {
        if (this.mapInstance) {
          this.mapInstance.destroy()
          this.mapInstance = null
        }
        this.mapMarker = null
        this.destroyPickerMap()
      } catch (error) {
        console.warn('销毁地图实例失败:', error)
      }
    },

    // ============== 图片管理相关方法 ==============

    selectMainImage() {
      this.imageSelectorVisible = true
    },

    handleImageSelectorClose() {
      console.log('图片选择器关闭')
      this.imageSelectorVisible = false
    },

    openImageUploader() {
      this.loadImageCategories().then(() => {
        this.imageUploaderVisible = true
      })
    },

    async loadImageCategories() {
      try {
        const res = await api.image_getImageCategoryList({})
        if (res.data.code === '000' && res.data.data && res.data.data.length > 0) {
          this.imageUploaderCategory = res.data.data[0]
        }
      } catch (error) {
        console.error('加载图片分类失败:', error)
      }
    },

    handleImageSelect(images) {
      console.log('图片选择结果:', images)
      if (images && images.length > 0) {
        const image = images[0]
        this.form.imageId = image.id
        this.mainImageUrl = image.url

        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate('imageId')
        }

        this.$message.success('图片选择成功')
      }
      // 确保选择器关闭
      this.imageSelectorVisible = false
    },

    handleImageUploadComplete() {
      console.log('图片上传完成')
      this.$message.success('图片上传成功，请在图片库中选择已上传的图片')
      // 先关闭上传器
      this.imageUploaderVisible = false
      // 延迟后打开选择器
      setTimeout(() => {
        this.imageSelectorVisible = true
      }, 500)
    },

    removeMainImage() {
      this.form.imageId = null
      this.mainImageUrl = ''
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate('imageId')
      }
      this.$message.info('已移除主图')
    },

    async loadMainImage(imageId) {
      if (!imageId) return
      if (this.mainImageUrl && this.mainImageUrl !== '') {
        console.log('主图URL已存在，跳过加载')
        return
      }

      try {
        let res = await api.image_getImageById({ id: imageId })
        console.log('加载主图API响应:', res)

        if (res.data.code === '000' && res.data.data) {
          const image = res.data.data
          this.setImageUrl(image)
        } else {
          console.warn('通过ID获取图片失败，尝试从列表获取')
          await this.loadImageFromList(imageId)
        }
      } catch (error) {
        console.error('加载主图失败，尝试其他方法:', error)
        await this.loadImageFromList(imageId)
      }
    },

    async loadImageFromList(imageId) {
      try {
        const res = await api.image_getImageList({
          page: 1,
          pageSize: 1,
          id: imageId
        })

        if (res.data.code === '000' && res.data.data && res.data.data.list && res.data.data.list.length > 0) {
          const image = res.data.data.list[0]
          this.setImageUrl(image)
        } else {
          console.warn('未找到图片数据，ID:', imageId)
          this.$message.warning('主图数据加载失败，请重新选择')
        }
      } catch (error) {
        console.error('从列表加载主图失败:', error)
        this.$message.warning('加载主图失败，请重新选择')
      }
    },

    setImageUrl(image) {
      let imageUrl = ''
      if (image.url) {
        imageUrl = image.url
      } else if (image.filepath) {
        imageUrl = image.filepath
      }
      this.mainImageUrl = imageUrl
      console.log('主图URL设置成功:', imageUrl)
    },

    // ============== 初始化页面 ==============
    async initializePage() {
      if (this.isInitializing) return

      this.isInitializing = true
      try {
        await this.loadUserInfo()
        await this.loadCategories()
        await this.loadStoreData()
        await this.loadAdditionalData()

        const loaded = await this.loadTencentMapSDK()
        if (loaded && this.form.longitude && this.form.latitude) {
          this.showMapPreview = true
          this.$nextTick(() => {
            setTimeout(() => {
              this.initMapPreview()
            }, 200)
          })
        }
      } catch (error) {
        console.error('页面初始化失败:', error)
        this.$message.error('页面初始化失败')
      } finally {
        this.isInitializing = false
      }
    },

    // ============== 用户信息加载 ==============
    async loadUserInfo() {
      this.loadingUserInfo = true
      console.log('开始加载用户信息...')

      try {
        const cachedUserInfo = localStorage.getItem('user_info')
        if (cachedUserInfo) {
          try {
            this.userInfo = JSON.parse(cachedUserInfo)
            console.log('从缓存加载用户信息:', this.userInfo)
            return
          } catch (e) {
            console.error('解析缓存失败:', e)
          }
        }

        const res = await api.login_getAdminInfo(null, {})
        if (res.data.code === '000' && res.data.data) {
          this.userInfo = res.data.data
          localStorage.setItem('user_info', JSON.stringify(this.userInfo))
        } else {
          throw new Error(res.data.msg || '获取用户信息失败')
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        throw error
      } finally {
        this.loadingUserInfo = false
      }
    },

    // ============== 店铺数据加载 ==============
    async loadStoreData() {
      const targetStoreId = this.currentStoreId

      if (!targetStoreId) {
        console.log('没有店铺ID，进入新建店铺模式')
        this.resetFormToNew()
        return
      }

      if (!this.isSuperAdmin && this.userInfo && this.userInfo.merchantId !== targetStoreId) {
        console.warn('权限不足，跳转到自己的店铺')
        this.$router.replace({
          path: this.$route.path,
          query: { id: this.userInfo.merchantId }
        })
        return
      }

      this.loading = true
      try {
        const storeRes = await api.merchant_getMerchantDetail({ id: targetStoreId })
        if (storeRes.data.code === '000' && storeRes.data.data) {
          await this.fillFormData(storeRes.data.data)
        } else {
          this.$message.error('加载店铺数据失败: ' + (storeRes.data.msg || '未知错误'))
        }
      } catch (error) {
        console.error('加载店铺数据失败:', error)
        this.$message.error('加载店铺数据失败，请重试')
      } finally {
        this.loading = false
      }
    },

    async fillFormData(store) {
      Object.assign(this.form, {
        id: store.id || null,
        name: store.name || '',
        imageId: store.imageId || null,
        status: store.status || 1,
        industryCategoryId: store.industryId || null,
        storeCategoryId: store.categoryId || null,
        productCategoryId: store.productCategoryId || null,
        businessHours: store.businessHours || '',
        avgPrice: store.avgPrice ? (store.avgPrice / 100).toFixed(2) : 0,
        introduction: store.introduction || '',
        notice: store.notice || '',
        address: store.address || '',
        longitude: store.longitude || null,
        latitude: store.latitude || null,
        starRating: store.starRating || 0,
        monthlySales: store.monthlySales || 0,
        ratingCount: store.ratingCount || 0,
        productCount: store.productCount || 0,
        viewCount: store.viewCount || 0,
        collectCount: store.collectCount || 0,
        recommendScore: store.recommendScore || 0
      })

      this.fillCategoryDataToForm()
    },

    resetFormToNew() {
      this.form = {
        id: null,
        name: '',
        imageId: null,
        status: 1,
        industryCategoryId: null,
        storeCategoryId: null,
        productCategoryId: null,
        businessHours: '',
        avgPrice: 0,
        introduction: '',
        notice: '',
        address: '',
        longitude: null,
        latitude: null,
        starRating: 0,
        monthlySales: 0,
        ratingCount: 0,
        productCount: 0,
        viewCount: 0,
        collectCount: 0,
        recommendScore: 0
      }
      this.mainImageUrl = ''
      this.selectedTags = []
      this.storeCategoryOptions = []
      this.productCategoryOptions = []
    },

    // ============== 分类数据加载 ==============
    async loadCategories(retryCount = 0) {
      return this.deduplicateRequest('loadCategories', async () => {
        const maxRetries = 3

        try {
          this.categoryLoading = true
          console.log('开始加载三级分类数据...')

          const res = await api.merchantCategory_getMerchantCategories({
            countMerchant: false
          })

          if (res.data.code === '000' && res.data.data) {
            this.categoryTree = res.data.data || []
            this.industryOptions = this.categoryTree.map(category => ({
              id: category.id,
              name: category.name
            }))
            console.log('分类数据加载成功，数量:', this.categoryTree.length)
          } else {
            if (retryCount < maxRetries) {
              console.log(`分类数据加载失败，${retryCount + 1}秒后进行第${retryCount + 1}次重试`)
              setTimeout(() => {
                this.loadCategories(retryCount + 1)
              }, (retryCount + 1) * 1000)
            } else {
              console.error('获取分类数据失败:', res.data.msg)
              this.$message.error('加载分类数据失败: ' + (res.data.msg || '未知错误'))
              this.useDefaultCategories()
            }
          }
        } catch (error) {
          console.error('加载分类数据异常:', error)
          if (retryCount < maxRetries) {
            setTimeout(() => {
              this.loadCategories(retryCount + 1)
            }, (retryCount + 1) * 1000)
          } else {
            this.$message.error('加载分类数据失败，请刷新页面重试')
            this.useDefaultCategories()
          }
        } finally {
          this.categoryLoading = false
        }
      })
    },

    useDefaultCategories() {
      this.industryOptions = [
        { id: 1, name: '餐饮' },
        { id: 2, name: '零售' },
        { id: 3, name: '服务' }
      ]
    },

    fillCategoryDataToForm() {
      if (!this.form.industryCategoryId) return

      const selectedIndustry = this.categoryTree.find(item => item.id === this.form.industryCategoryId)
      if (selectedIndustry && selectedIndustry.children) {
        this.storeCategoryOptions = selectedIndustry.children.map(child => ({
          id: child.id,
          name: child.name
        }))
      }

      if (this.form.storeCategoryId && selectedIndustry && selectedIndustry.children) {
        const selectedStoreCategory = selectedIndustry.children.find(
            item => item.id === this.form.storeCategoryId
        )
        if (selectedStoreCategory && selectedStoreCategory.children) {
          this.productCategoryOptions = selectedStoreCategory.children.map(child => ({
            id: child.id,
            name: child.name
          }))
        }
      }
    },

    handleIndustryChange(industryId) {
      this.form.storeCategoryId = null
      this.form.productCategoryId = null
      this.storeCategoryOptions = []
      this.productCategoryOptions = []

      if (industryId) {
        const selectedIndustry = this.categoryTree.find(item => item.id === industryId)
        if (selectedIndustry && selectedIndustry.children) {
          this.storeCategoryOptions = selectedIndustry.children.map(child => ({
            id: child.id,
            name: child.name
          }))
        }
      }
    },

    handleStoreCategoryChange(storeCategoryId) {
      this.form.productCategoryId = null
      this.productCategoryOptions = []

      if (storeCategoryId && this.form.industryCategoryId) {
        const selectedIndustry = this.categoryTree.find(item => item.id === this.form.industryCategoryId)
        if (selectedIndustry && selectedIndustry.children) {
          const selectedStoreCategory = selectedIndustry.children.find(
              item => item.id === storeCategoryId
          )
          if (selectedStoreCategory && selectedStoreCategory.children) {
            this.productCategoryOptions = selectedStoreCategory.children.map(child => ({
              id: child.id,
              name: child.name
            }))
          }
        }
      }
    },

    // ============== 保存店铺资料 ==============
    async saveStoreProfile() {
      if (!this.$refs.formRef) return

      try {
        const valid = await this.$refs.formRef.validate()
        if (!valid) {
          this.$message.error('请检查表单必填项')
          return
        }

        if (!this.form.imageId) {
          this.$message.error('请选择店铺主图')
          return
        }
      } catch (validateError) {
        console.error('表单验证异常:', validateError)
        this.$message.error('表单验证失败，请检查必填项')
        return
      }

      this.loading = true
      try {
        const saveData = {
          id: this.form.id,
          name: this.form.name,
          imageId: this.form.imageId,
          status: this.form.status,
          industryId: this.form.industryCategoryId,
          categoryId: this.form.storeCategoryId,
          productCategoryId: this.form.productCategoryId,
          businessHours: this.form.businessHours,
          avgPrice: this.form.avgPrice ? Math.round(parseFloat(this.form.avgPrice)) : 0,
          introduction: this.form.introduction,
          notice: this.form.notice,
          address: this.form.address,
          longitude: this.form.longitude,
          latitude: this.form.latitude
        }

        if (this.selectedTags.length > 0) {
          saveData.tagIds = this.selectedTags.map(tag => tag.id)
        }

        let res
        if (this.form.id) {
          res = await api.merchant_editMerchant(saveData)
        } else {
          res = await api.merchant_addMerchant(saveData)
        }

        if (res.data.code === '000') {
          this.$message.success(this.form.id ? '店铺资料更新成功' : '店铺创建成功')

          if (!this.form.id && res.data.data && res.data.data.id) {
            this.$router.push({
              path: '/store/profile',
              query: { id: res.data.data.id }
            })
          } else {
            await this.loadStoreData()
          }
        } else {
          this.$message.error(res.data.msg || '保存失败')
        }
      } catch (error) {
        console.error('保存过程发生异常:', error)
        this.$message.error('保存失败，请重试: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    // ============== 标签数据加载 ==============
    async loadAdditionalData() {
      try {
        if (this.currentStoreId) {
          await this.loadMerchantTags()
        }
        await this.loadAllTags()
      } catch (error) {
        console.error('加载附加数据失败:', error)
      }
    },

    async loadMerchantTags() {
      if (!this.currentStoreId) return

      try {
        const res = await api.tag_getTagList({
          page: 1,
          pageSize: 100,
          search: '',
          merchantId: this.currentStoreId
        })

        if (res.data.code === '000' && res.data.data && res.data.data.list) {
          const tagList = res.data.data.list || []
          this.selectedTags = tagList.map(tag => ({
            id: tag.id,
            name: tag.name,
            typeCode: tag.typeCode || 1,
            typeText: tag.typeText || '未知类型'
          }))
        }
      } catch (error) {
        console.error('加载店铺标签失败:', error)
        this.selectedTags = []
      }
    },

    async loadAllTags() {
      try {
        const res = await api.tag_getAllTags({})
        if (res.data.code === '000' && res.data.data && res.data.data.list) {
          const tagList = res.data.data.list || []
          const tagCategoriesMap = {}

          tagList.forEach(tag => {
            const typeCode = tag.typeCode || 1
            const typeText = tag.typeText || '未知类型'

            if (!tagCategoriesMap[typeCode]) {
              tagCategoriesMap[typeCode] = {
                typeCode: typeCode,
                name: typeText,
                tags: []
              }
            }

            tagCategoriesMap[typeCode].tags.push({
              id: tag.id,
              name: tag.name,
              typeCode: typeCode,
              typeText: typeText
            })
          })

          this.tagCategories = Object.values(tagCategoriesMap)
              .sort((a, b) => a.typeCode - b.typeCode)

          if (this.tagCategories.length > 0) {
            this.activeTagCategory = this.tagCategories[0].typeCode.toString()
          }
        } else {
          this.useDefaultTags()
        }
      } catch (error) {
        console.error('加载标签失败:', error)
        this.useDefaultTags()
      }
    },

    useDefaultTags() {
      const defaultTags = [
        { id: 1, name: '网红打卡', typeCode: 1, typeText: '店铺特色' },
        { id: 2, name: '老字号', typeCode: 1, typeText: '店铺特色' },
        { id: 3, name: '特色小吃', typeCode: 1, typeText: '店铺特色' },
        { id: 4, name: '连锁品牌', typeCode: 1, typeText: '店铺特色' },
        { id: 5, name: '24小时营业', typeCode: 2, typeText: '服务标签' },
        { id: 6, name: '免费WiFi', typeCode: 2, typeText: '服务标签' },
        { id: 7, name: '可预订', typeCode: 2, typeText: '服务标签' },
        { id: 8, name: '可外带', typeCode: 2, typeText: '服务标签' },
        { id: 9, name: '环境优雅', typeCode: 3, typeText: '环境标签' },
        { id: 10, name: '干净卫生', typeCode: 3, typeText: '环境标签' },
        { id: 11, name: '停车方便', typeCode: 3, typeText: '环境标签' },
        { id: 12, name: '商场内', typeCode: 3, typeText: '环境标签' }
      ]

      const tagCategoriesMap = {}
      defaultTags.forEach(tag => {
        const typeCode = tag.typeCode
        const typeText = tag.typeText

        if (!tagCategoriesMap[typeCode]) {
          tagCategoriesMap[typeCode] = {
            typeCode: typeCode,
            name: typeText,
            tags: []
          }
        }

        tagCategoriesMap[typeCode].tags.push({
          id: tag.id,
          name: tag.name,
          typeCode: typeCode,
          typeText: typeText
        })
      })

      this.tagCategories = Object.values(tagCategoriesMap)
          .sort((a, b) => a.typeCode - b.typeCode)
    },

    checkTagDataConsistency() {
      if (this.selectedTags.length > 10) {
        this.selectedTags = this.selectedTags.slice(0, 10)
        this.$message.warning('已选标签超过10个限制，已自动保留前10个')
      }

      const tagIds = new Set()
      const uniqueTags = []
      this.selectedTags.forEach(tag => {
        if (!tagIds.has(tag.id)) {
          tagIds.add(tag.id)
          uniqueTags.push(tag)
        }
      })
      if (uniqueTags.length !== this.selectedTags.length) {
        this.selectedTags = uniqueTags
        this.$message.warning('发现重复标签，已自动去重')
      }

      const allTagIds = []
      this.tagCategories.forEach(category => {
        category.tags.forEach(tag => {
          allTagIds.push(tag.id)
        })
      })

      const invalidTags = this.selectedTags.filter(tag => !allTagIds.includes(tag.id))
      if (invalidTags.length > 0) {
        this.selectedTags = this.selectedTags.filter(tag => allTagIds.includes(tag.id))
        this.$message.warning(`发现 ${invalidTags.length} 个无效标签，已自动移除`)
      }
    },

    // ============== 其他方法 ==============
    gotoQualification() {
      if (this.form.id) {
        this.$router.push({
          path: 'Qualification',
          query: { id: this.form.id }
        })
      }
    },

    openImageManager() {
      if (this.form.id) {
        this.$router.push({
          path: 'Images',
          query: { id: this.form.id }
        })
      }
    },

    getTagType(typeCode) {
      const types = {
        1: 'success',
        2: 'warning',
        3: 'info'
      }
      return types[typeCode] || ''
    },

    isTagSelected(tagId) {
      return this.selectedTags.some(tag => tag.id === tagId)
    },

    toggleTag(tag) {
      if (this.isTagSelected(tag.id)) {
        this.removeTag(tag.id)
      } else {
        if (this.selectedTags.length >= 10) {
          this.$message.warning('最多只能选择10个标签')
          return
        }
        this.selectedTags.push({
          id: tag.id,
          name: tag.name,
          typeCode: tag.typeCode || 1,
          typeText: tag.typeText || '未知类型'
        })
      }
    },

    removeTag(tagId) {
      const index = this.selectedTags.findIndex(tag => tag.id === tagId)
      if (index > -1) {
        this.selectedTags.splice(index, 1)
      }
    },

    handleTagCategoryChange() {},

    handlePriceChange(value) {
      if (value === null || value === undefined) {
        this.form.avgPrice = 0
        return
      }
      this.form.avgPrice = Number(value).toFixed(2)
    },

    previewStore() {
      if (!this.form.id) {
        this.$message.warning('请先保存店铺信息后再预览')
        return
      }
      window.open(`/store/preview/${this.form.id}`, '_blank')
    },

    // ============== 图标相关方法 ==============
    showAllIcons() {
      this.iconDialog.visible = true
      this.iconDialog.selectedIconId = null
      this.iconDialog.selectedIcon = null
    },

    handleIconDialogOpened() {
      this.loadIcons()
    },

    async loadIcons() {
      try {
        this.iconDialog.loading = true
        const params = {
          page: this.iconDialog.page,
          pageSize: this.iconDialog.pageSize
        }

        if (this.iconDialog.filter.search) {
          params.search = this.iconDialog.filter.search
        }

        const res = await api.industryIcon_getAllIcons(params)
        if (res.data.code === '000' && res.data.data) {
          this.iconDialog.list = res.data.data.list || []
          this.iconDialog.total = res.data.data.totalCount || 0
          this.iconDialog.totalUsageCount = this.iconDialog.list.reduce((sum, icon) => {
            return sum + (icon.usageCount || 0)
          }, 0)
        } else {
          this.iconDialog.list = []
          this.iconDialog.total = 0
        }
      } catch (error) {
        console.error('加载图标列表异常:', error)
        this.iconDialog.list = []
        this.iconDialog.total = 0
      } finally {
        this.iconDialog.loading = false
      }
    },

    selectIcon(icon) {
      this.iconDialog.selectedIconId = icon.id
      this.iconDialog.selectedIcon = icon
    },

    useSelectedIcon() {
      if (!this.iconDialog.selectedIcon) {
        this.$message.warning('请先选择一个图标')
        return
      }

      const icon = this.iconDialog.selectedIcon
      if (icon.iconChar) {
        navigator.clipboard.writeText(icon.iconChar)
            .then(() => {
              this.$message.success(`已复制图标字符: ${icon.iconChar}`)
            })
            .catch(err => {
              console.error('复制失败:', err)
              this.$message.success(`图标已选择: ${icon.iconName}`)
            })
      } else {
        this.$message.success(`图标已选择: ${icon.iconName}`)
      }
    },

    resetIconFilter() {
      this.iconDialog.filter.search = ''
      this.iconDialog.page = 1
      this.loadIcons()
    },

    handleIconPageSizeChange(size) {
      this.iconDialog.pageSize = size
      this.iconDialog.page = 1
      this.loadIcons()
    },

    handleIconPageChange(page) {
      this.iconDialog.page = page
      this.loadIcons()
    }
  }
}
</script>

<style scoped>
.store-profile-container {
  display: flex;
  height: 100%;
  width: 100%;
  gap: 20px;
  padding: 20px;
  box-sizing: border-box;
  background-color: #f5f7fa;
}

.sidebar {
  width: 200px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  height: fit-content;
}

.sidebar-header {
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.sidebar-actions {
  margin: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sidebar-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
}

.icon-view-btn {
  color: #666;
}

.primary-btn {
  color: #fff !important;
}

.main-content {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  padding: 0;
}

.profile-card {
  border: none;
  box-shadow: none;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.profile-content {
  padding: 20px;
}

.user-info-tip {
  margin-bottom: 20px;
}

.section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.main-image-manager .image-placeholder-container {
  margin-bottom: 12px;
}

.main-image-manager .image-actions {
  margin-bottom: 12px;
  display: flex;
  gap: 12px;
}

.main-image-manager .image-tips {
  color: #909399;
  font-size: 13px;
  line-height: 1.8;
}

.main-image-manager .image-tips p {
  margin: 4px 0;
}

.category-cascade {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.price-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.price-unit {
  font-size: 14px;
  color: #666;
}

.price-tip {
  font-size: 12px;
  color: #999;
  margin-left: 10px;
}

.introduction-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.editor-tips {
  font-size: 12px;
  color: #999;
  padding: 8px 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.tags-management-row {
  display: flex;
  gap: 30px;
}

.tags-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.selected-tags-column {
  min-width: 300px;
}

.available-tags-column {
  min-width: 500px;
}

.tags-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.tags-tip {
  font-size: 12px;
  color: #909399;
}

.tags-list {
  flex: 1;
  min-height: 200px;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background-color: #fafafa;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-content: flex-start;
}

.tag-item {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 4px;
}

.tag-type-text {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.tag-type-badge {
  font-size: 10px;
  color: #fff;
  background-color: #909399;
  padding: 1px 4px;
  border-radius: 2px;
  margin-left: 6px;
}

.category-tags {
  min-height: 200px;
  max-height: 300px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-content: flex-start;
}

.tag-option {
  cursor: pointer;
  transition: all 0.2s;
}

.tag-option:hover {
  transform: translateY(-2px);
}

.tag-selected {
  background-color: #67c23a !important;
  color: #fff !important;
}

.selected-icon {
  font-size: 12px;
  margin-left: 4px;
}

.address-input {
  width: 100%;
}

.coordinates {
  display: flex;
  gap: 20px;
  margin-top: 16px;
}

.map-preview {
  margin-top: 20px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.map-tips {
  padding: 8px 12px;
  font-size: 12px;
  color: #666;
  background-color: #f9f9f9;
  border-top: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-viewer-dialog {
  --el-dialog-body-padding: 20px;
}

.icon-viewer-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 70vh;
  overflow: hidden;
}

.icon-stats {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.icon-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.icon-list-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.icon-loading {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #666;
  gap: 8px;
}

.icon-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  gap: 8px;
}

.icon-empty i {
  font-size: 48px;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.icon-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.icon-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.1);
}

.icon-item.selected {
  border-color: #67c23a;
  background-color: rgba(103, 194, 58, 0.05);
}

.icon-display {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 12px;
}

.icon-char {
  font-size: 32px;
  color: #409eff;
}

.icon-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.icon-name {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.icon-category {
  display: flex;
  gap: 8px;
  font-size: 12px;
}

.main-category {
  color: #409eff;
  background-color: rgba(64, 158, 255, 0.1);
  padding: 2px 6px;
  border-radius: 2px;
}

.sub-category {
  color: #67c23a;
  background-color: rgba(103, 194, 58, 0.1);
  padding: 2px 6px;
  border-radius: 2px;
}

.icon-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.icon-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.selected-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #67c23a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.icon-pagination {
  display: flex;
  justify-content: center;
  padding: 10px 0;
  border-top: 1px solid #ebeef5;
  margin-top: 10px;
}

.selected-icon-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.icon-preview {
  font-size: 24px;
  color: #409eff;
}

@media (max-width: 1200px) {
  .store-profile-container {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
  }

  .sidebar-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .tags-management-row {
    flex-direction: column;
  }

  .tags-column {
    min-width: auto;
  }

  .tags-list {
    min-height: 150px;
  }

  .category-tags {
    min-height: 150px;
    max-height: 200px;
  }
}

@media (max-width: 768px) {
  .coordinates {
    flex-direction: column;
    gap: 16px;
  }

  .icon-grid {
    grid-template-columns: 1fr;
  }
}
</style>